<template>
  <div class="chain-reaction-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$k = 中子产生率 / 中子消耗率$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>当前代数 Generations</label>
            <span class="val">{{ generation }}</span>
          </div>
          <div class="hud-item" :class="kFactor >= 1 ? 'orange' : 'gray'">
            <label>增殖系数 k</label>
            <span class="val">{{ kFactor.toFixed(2) }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <radialGradient id="u235-grad"><stop offset="0%" stop-color="#22c55e"/><stop offset="100%" stop-color="#064e3b"/></radialGradient>
             <radialGradient id="flash-grad"><stop offset="0%" stop-color="#fff"/><stop offset="100%" stop-color="#fbbf24" opacity="0"/></radialGradient>
          </defs>

          <!-- 原子核阵列 (Target Nuclei) -->
          <g v-for="(n, i) in nuclei" :key="'n'+i">
             <circle :cx="n.x" :cy="n.y" :r="n.status==='fissioned' ? 0 : 8" fill="url(#u235-grad)" opacity="0.8">
                <title>U-235 原子核</title>
             </circle>
             <circle v-if="n.flash" :cx="n.x" :cy="n.y" :r="n.flashR" fill="url(#flash-grad)" />
          </g>

          <!-- 飞行的中子粒子流 -->
          <g v-for="z in neutrons" :key="z.id">
             <circle :cx="z.x" :cy="z.y" r="2" fill="#fbbf24" filter="url(#glow-n)" />
          </g>

          <defs>
             <filter id="glow-n"><feGaussianBlur stdDeviation="1.5" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="status-overlay" v-if="generation > 15 && kFactor > 1.2">
           <span class="danger-tag">超临界状态：核爆反应中</span>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节倍增系数 (k)：{{ kFactor.toFixed(2) }}</label>
                 <el-slider v-model="kFactor" :min="0.5" :max="2.0" :step="0.05" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="ignite">引发裂变 (第一代中子)</el-button>
                 <el-button size="small" @click="reset">重置实验</el-button>
              </div>
           </div>
        </div>
        
        <!-- 核心加固：超高对比度红色卡片，确保文字看清 -->
        <div class="discovery-card-discovery-danger" v-latex>
           <div class="icon-wrap"><i class="el-icon-warning"></i></div>
           <div class="text-content">
             <p><strong>实战物理：</strong>一个 U-235 裂变释放 2-3 个中子。<strong>关键逻辑：</strong>当滑块 $ k < 1 $ 时，反应会由于中子流失而熄灭；当 $ k > 1 $ 时，反应会成指数级爆炸式增长。<strong>观察现象：</strong>开启后，原子核连锁“闪烁”代表能量释放，这正是原子弹与核电站（受控裂变）的本质区别。</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'NuclearChainReactionViz',
  data() {
    return {
      kFactor: 1.1,
      nuclei: [],
      neutrons: [],
      generation: 0,
      isRunning: false,
      nextId: 0
    }
  },
  mounted() { this.initNuclei(); },
  methods: {
    initNuclei() {
       this.nuclei = [];
       for(let i=0; i<60; i++) {
          this.nuclei.push({ x: 20 + Math.random()*410, y: 20 + Math.random()*200, status: 'stable', flash: false, flashR: 0 });
       }
    },
    ignite() {
       if (this.isRunning) return;
       this.isRunning = true;
       this.spawnNeutron(50, 120);
       this.animate();
    },
    spawnNeutron(x, y) {
       const angle = Math.random() * Math.PI * 2;
       this.neutrons.push({ id: this.nextId++, x, y, vx: Math.cos(angle)*3, vy: Math.sin(angle)*3 });
    },
    animate() {
       if (!this.isRunning) return;
       for(let i=this.neutrons.length-1; i>=0; i--) {
          const z = this.neutrons[i];
          z.x += z.vx; z.y += z.vy;

          if (z.x < 0 || z.x > 450 || z.y < 0 || z.y > 240) {
             this.neutrons.splice(i, 1);
             continue;
          }

          this.nuclei.forEach(n => {
             if (n.status === 'stable' && Math.hypot(n.x-z.x, n.y-z.y) < 8) {
                n.status = 'fissioned';
                n.flash = true; n.flashR = 20;
                this.neutrons.splice(i, 1);
                this.generation++;
                // Multiply based on kFactor
                const spawnCount = Math.random() < (this.kFactor - 1) ? 2 : 1;
                for(let j=0; j<spawnCount; j++) this.spawnNeutron(n.x, n.y);
             }
          });
       }

       this.nuclei.forEach(n => { if(n.flash) n.flashR *= 0.8; if(n.flashR < 2) n.flash = false; });
       if (this.neutrons.length === 0 && this.generation > 0) this.isRunning = false;
       this.animFrame = requestAnimationFrame(this.animate);
    },
    reset() {
       this.isRunning = false;
       cancelAnimationFrame(this.animFrame);
       this.initNuclei();
       this.neutrons = [];
       this.generation = 0;
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
.chain-reaction-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.gray { border-left: 3px solid #64748b; opacity: 0.6; } } }
.status-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); .danger-tag { background: rgba(239, 68, 68, 0.9); color: white; padding: 8px 16px; border-radius: 8px; font-weight: 900; box-shadow: 0 0 20px #ef4444; } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }

/* 重点加固：深红高对比度卡片，确保文字可见 */
.discovery-card-discovery-danger { 
  background: #fef2f2; 
  padding: 12px 16px; 
  border-radius: 10px; 
  border: 1px solid #fee2e2; 
  display: flex; gap: 14px; 
  align-items: center;
  i { color: #dc2626; font-size: 1.4rem; }
  p { 
    margin: 0; 
    font-size: 11.5px; 
    color: #7f1d1d; // 极深红色文字，对比度爆表
    line-height: 1.6;
    strong { color: #450a0a; font-weight: 900; }
  }
}
</style>
